/// <reference types="google-maps" />

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gis } from 'Douleutaras/components/Forms/gis.class';

import { LocationMapPanelProps } from './LocationMapPanel';

// State reported to the parent form/modal while the map picker is open, so the
// form can hide its title/note, the modal footer can render a Confirm button, and
// the modal close button can dismiss the map view.
interface MapViewState {
  confirmDisabled: boolean;
  confirm(): void;
  close(): void;
}

interface UseMapPickerParams {
  // Reverse-geocodes + validates a picked location, returning whether it is served.
  checkLocation(location: Gis): Promise<boolean | undefined>;
  // Commits the picked address description into the form's `address` field.
  setAddressValue(address: string): void;
  onMapViewChange?(state?: MapViewState): void;
}

interface UseMapPickerResult {
  isMapView: boolean;
  panelProps: Pick<LocationMapPanelProps, 'addressValue' | 'center' | 'resolveCenter' | 'errorMessage' | 'onPick'>;
  openMapView(): void;
}

/**
 * Owns the location step's map-picker view state. The step has two views: the
 * default autocomplete + target button, and this inline map picker. Map
 * interactions are kept local and only committed on Confirm.
 */
export const useMapPicker = ({
                               checkLocation,
                               setAddressValue,
                               onMapViewChange
}: UseMapPickerParams): UseMapPickerResult => {
  const { t } = useTranslation();

  const [ isMapView, setIsMapView ] = useState(false);
  const [ mapPickedGis, setMapPickedGis ] = useState<Gis>();
  const [ mapAddress, setMapAddress ] = useState<string>();
  const [ mapCenter, setMapCenter ] = useState<google.maps.LatLngLiteral>();
  const [ mapError, setMapError ] = useState<string>();
  // Whether the currently picked map location passed the geo check (drives the
  // Confirm button's enabled state).
  const [ mapIsValid, setMapIsValid ] = useState(false);
  // Asks the map to reverse-geocode + validate the current center immediately —
  // set only when seeding the map with the user's device location.
  const [ mapResolveCenter, setMapResolveCenter ] = useState(false);

  // Open the map picker. Seed it with the user's current device location when the
  // browser grants access, so the map starts there and that location is validated
  // immediately (see mapResolveCenter → LocationMap).
  const openMapView = () => {
    setIsMapView(true);

    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        setMapCenter({ lat: position.coords.latitude, lng: position.coords.longitude });
        setMapResolveCenter(true);
      },
      () => undefined
    );
  };

  // Map-view picks (drag, search, or device location) apply and validate the address
  // right away so the Confirm button reflects whether the area is served.
  const handleMapPick = async (location: Gis) => {
    if (location.longitude === undefined || location.latitude === undefined) {
      return;
    }
    setMapPickedGis(location);
    setMapAddress(location.description);
    setMapCenter({ lat: location.latitude, lng: location.longitude });
    setMapResolveCenter(false);
    setAddressValue(location.description);

    const isServed = await checkLocation(location);
    setMapIsValid(Boolean(isServed));
    setMapError(isServed ? undefined : t('T.VALIDATOR.Location is invalid'));
  };

  // Close: leave the map view. A picked-but-invalid location keeps its
  // `server.location` error (set by doGeoCheck) so it stays visible under the
  // autocomplete input on the location step.
  const resetMapView = () => {
    setIsMapView(false);
    setMapPickedGis(undefined);
    setMapAddress(undefined);
    setMapCenter(undefined);
    setMapError(undefined);
    setMapIsValid(false);
    setMapResolveCenter(false);
  };

  // Confirm: the picked address was already applied and validated on pick, so just
  // leave the map view. Guarded by the disabled Confirm button, re-checked here.
  const confirmMapView = () => {
    if (!mapPickedGis || !mapIsValid) {
      return;
    }
    setIsMapView(false);
    setMapError(undefined);
  };

  // Report the map-picker view state (with confirm/close handlers) upward so the form
  // can hide its title/note, the modal footer can render the Confirm button, and the
  // modal close button can dismiss the map view.
  useEffect(() => {
    onMapViewChange?.(isMapView
      ? { confirm: confirmMapView, confirmDisabled: !mapIsValid, close: resetMapView }
      : undefined);
  }, [isMapView, mapIsValid]);

  // Safety net: clear the reported map-view state if the step unmounts while open.
  useEffect(() => () => onMapViewChange?.(undefined), []);

  return {
    isMapView,
    openMapView,
    panelProps: {
      addressValue: mapAddress,
      center: mapCenter,
      resolveCenter: mapResolveCenter,
      errorMessage: mapError,
      onPick: handleMapPick
    }
  };
};
